import { getSavedState, removeState, saveState } from '@state/helpers'
import { getOnboardingStatus } from '@src/api/loOnboarding'

const SHOULD_SHOW_WELCOME_OVERLAY_KEY = 'onboarding.shouldShowWelcomeOverlay'
const HAS_PENDING_ONBOARDING_KEY = 'onboarding.hasPendingOnboarding'
const ONBOARDING_TYPE_KEY = 'onboarding.onboardingType'
const WELCOME_OVERLAY_PROMPTED_ON_KEY = 'onboarding.welcomeOverlayPromptedOn'
const WELCOME_OVERLAY_VARIANTS = ['first', 'rejected', 'returning']
const ONBOARDING_TYPES = ['lo', 'office']

export const ONBOARDING_TYPE_LO = 'lo'
export const ONBOARDING_TYPE_OFFICE = 'office'

function normalizeOnboardingType(value) {
  return ONBOARDING_TYPES.includes(value) ? value : null
}

export function getWelcomeOverlayPromptDateKey(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function normalizeWelcomeOverlayVariant(variant) {
  return WELCOME_OVERLAY_VARIANTS.includes(variant) ? variant : 'first'
}

function normalizePromptedOn(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }

  return value
}

export function hasPromptedWelcomeOverlayToday(promptedOn, variant, date = new Date()) {
  return (
    normalizePromptedOn(promptedOn)[normalizeWelcomeOverlayVariant(variant)] === getWelcomeOverlayPromptDateKey(date)
  )
}

export const state = {
  allSteps: [],
  docusignDocuments: [],
  hasPendingOnboarding: getSavedState(HAS_PENDING_ONBOARDING_KEY, false),
  onboardingType: normalizeOnboardingType(getSavedState(ONBOARDING_TYPE_KEY, null)),
  shouldShowWelcomeOverlay: getSavedState(SHOULD_SHOW_WELCOME_OVERLAY_KEY, false),
  welcomeOverlayPromptedOn: normalizePromptedOn(getSavedState(WELCOME_OVERLAY_PROMPTED_ON_KEY, {})),
}

export const getters = {
  allSteps(state) {
    return state.allSteps
  },
  docusignDocuments(state) {
    return state.docusignDocuments
  },
  hasPendingOnboarding(state, getters, rootState, rootGetters) {
    return rootGetters['user/canAccessOnboarding'] && state.hasPendingOnboarding
  },
  onboardingType(state) {
    return state.onboardingType
  },
  isLoOnboarding(state) {
    return state.onboardingType === ONBOARDING_TYPE_LO
  },
  shouldShowWelcomeOverlay(state) {
    return state.shouldShowWelcomeOverlay
  },
  hasShownWelcomeOverlayToday(state) {
    return (variant) => hasPromptedWelcomeOverlayToday(state.welcomeOverlayPromptedOn, variant)
  },
}

export const mutations = {
  SET_ALL_STEPS(state, steps) {
    state.allSteps = steps
  },
  SET_DOCUSIGN_DOCUMENTS(state, documents) {
    state.docusignDocuments = documents || []
  },
  SET_HAS_PENDING_ONBOARDING(state, value) {
    const hasPendingOnboarding = Boolean(value)
    state.hasPendingOnboarding = hasPendingOnboarding
    saveState(HAS_PENDING_ONBOARDING_KEY, hasPendingOnboarding)
  },
  SET_ONBOARDING_TYPE(state, value) {
    const onboardingType = normalizeOnboardingType(value)
    state.onboardingType = onboardingType

    if (onboardingType) {
      saveState(ONBOARDING_TYPE_KEY, onboardingType)
      return
    }

    removeState(ONBOARDING_TYPE_KEY)
  },
  SET_SHOULD_SHOW_WELCOME_OVERLAY(state, value) {
    state.shouldShowWelcomeOverlay = value
    if (value) {
      saveState(SHOULD_SHOW_WELCOME_OVERLAY_KEY, true)
      return
    }

    removeState(SHOULD_SHOW_WELCOME_OVERLAY_KEY)
  },
  MARK_WELCOME_OVERLAY_PROMPTED(state, variant) {
    const promptedOn = {
      ...normalizePromptedOn(state.welcomeOverlayPromptedOn),
      [normalizeWelcomeOverlayVariant(variant)]: getWelcomeOverlayPromptDateKey(),
    }

    state.welcomeOverlayPromptedOn = promptedOn
    saveState(WELCOME_OVERLAY_PROMPTED_ON_KEY, promptedOn)
  },
}

export const actions = {
  async refreshPendingOnboarding({ commit, rootGetters }) {
    if (!rootGetters['user/canAccessOnboarding']) {
      commit('SET_HAS_PENDING_ONBOARDING', false)
      commit('SET_ONBOARDING_TYPE', null)
      return false
    }

    const { data } = await getOnboardingStatus()
    const hasPendingOnboarding = Boolean(data.hasPendingOnboarding)
    commit('SET_HAS_PENDING_ONBOARDING', hasPendingOnboarding)
    commit('SET_ONBOARDING_TYPE', hasPendingOnboarding ? data.onboardingType : null)

    return hasPendingOnboarding
  },
}
